if (!customElements.get('l20-video-player')) {
  class L20VideoPlayer extends HTMLElement {
    connectedCallback() {
      this.playButton = this.querySelector(
        '.l20-second-video__play, .l20-video-text__play'
      );

      this.media = Array.from(
        this.querySelectorAll(
          '.l20-second-video__video, .l20-video-text__video'
        )
      );

      this.videos = this.media.filter(
        (item) => item instanceof HTMLVideoElement
      );

      this.youtubeIframes = this.media.filter(
        (item) => item instanceof HTMLIFrameElement
      );

      if (!this.playButton || this.media.length === 0) return;

      this.handlePlay = () => this.play(false);
      this.handleEnded = this.reset.bind(this);

      this.playButton.addEventListener('click', this.handlePlay);

      this.videos.forEach((video) => {
        video.addEventListener('ended', this.handleEnded);
      });

      if (this.dataset.autoplay === 'true') {
        this.play(true);
      }
    }

    disconnectedCallback() {
      this.playButton?.removeEventListener('click', this.handlePlay);

      this.videos?.forEach((video) => {
        video.removeEventListener('ended', this.handleEnded);
      });
    }

    get activeMedia() {
      return (
        this.media.find((item) => {
          const style = getComputedStyle(item);

          return (
            style.display !== 'none' &&
            style.visibility !== 'hidden'
          );
        }) || this.media[0]
      );
    }

    async play(isAutoplay = false) {
      const activeMedia = this.activeMedia;

      if (!activeMedia) return;

      /*
       * 暂停其它 HTML5 video
       */
      this.videos.forEach((video) => {
        if (video !== activeMedia) {
          video.pause();
        }
      });

      /*
       * 暂停其它 YouTube iframe
       */
      this.youtubeIframes.forEach((iframe) => {
        if (iframe !== activeMedia) {
          this.pauseYouTube(iframe);
        }
      });

      /*
       * YouTube
       */
      if (activeMedia instanceof HTMLIFrameElement) {
        this.playYouTube(activeMedia, isAutoplay);
        return;
      }

      /*
       * Shopify video
       */
      try {
        if (isAutoplay) {
          activeMedia.muted = true;
        }

        await activeMedia.play();

        this.classList.add('is-playing');
      } catch (error) {
        this.classList.remove('is-playing');

        console.warn(
          'Unable to play L20 video.',
          error
        );
      }
    }

    playYouTube(iframe, isAutoplay = false) {
      if (!iframe) return;

      if (this.classList.contains('is-loading')) return;

      if (!iframe.getAttribute('src') && iframe.dataset.videoSrc) {
        const videoUrl = new URL(
          iframe.dataset.videoSrc,
          window.location.href
        );

        videoUrl.searchParams.set('autoplay', '1');

        if (isAutoplay) {
          videoUrl.searchParams.set('mute', '1');
        }

        this.classList.add('is-loading');
        this.playButton?.setAttribute('aria-busy', 'true');

        iframe.addEventListener(
          'load',
          () => {
            this.classList.remove('is-loading');
            this.classList.add('is-playing');
            this.playButton?.removeAttribute('aria-busy');
          },
          { once: true }
        );

        iframe.src = videoUrl.toString();
        delete iframe.dataset.videoSrc;
        return;
      }

      if (!iframe?.contentWindow) return;

      this.classList.add('is-playing');
      this.classList.remove('is-loading');

      this.playButton?.removeAttribute('aria-busy');

      /*
       * 浏览器自动播放要求静音
       */
      if (isAutoplay) {
        this.sendYouTubeCommand(
          iframe,
          'mute'
        );
      } else {
        /*
         * 用户主动点击播放时恢复声音
         */
        this.sendYouTubeCommand(
          iframe,
          'unMute'
        );
      }

      /*
       * 真正调用 YouTube Player API
       */
      this.sendYouTubeCommand(
        iframe,
        'playVideo'
      );
    }

    pauseYouTube(iframe) {
      this.sendYouTubeCommand(
        iframe,
        'pauseVideo'
      );
    }

    sendYouTubeCommand(
      iframe,
      func,
      args = []
    ) {
      if (!iframe?.contentWindow) return;

      iframe.contentWindow.postMessage(
        JSON.stringify({
          event: 'command',
          func,
          args
        }),
        '*'
      );
    }

    resetYouTubeIframe(iframe) {
      this.sendYouTubeCommand(
        iframe,
        'stopVideo'
      );
    }

    reset() {
      this.classList.remove(
        'is-loading',
        'is-playing'
      );

      this.playButton?.removeAttribute(
        'aria-busy'
      );

      this.playButton?.focus({
        preventScroll: true
      });
    }
  }

  customElements.define(
    'l20-video-player',
    L20VideoPlayer
  );
}